import React from 'react';
import { useState } from 'react';
import Layout from '../../Layout/Layout';
import { Container, Row, Col } from 'reactstrap';
import TitleBar from '../../Layout/TitleBar';
import {
  CookbookMealListEdge,
  useTranslator,
  withLoading,
  SearchVariables,
} from '@front-activerse/common-components';
import FavMealsFilters from './CookbookFavMealsFilters';
import CookbookMealItem from '../components/CookbookMealItem';
import UniversalPlaceholder from '../../Layout/UniversalPlaceholder';

import { useAppContext } from '@front-activerse/common-components';

interface ICookbookFavMealsContainer {
  meals: CookbookMealListEdge[];
  addToFavorites: (mealId: number) => Promise<unknown>;
  removeFromFavorites: (mealId: number) => Promise<unknown>;
  refetch: (searchVariables: SearchVariables) => Promise<unknown>;
}

export const CookbookFavMealsContainer: React.FC<ICookbookFavMealsContainer> = ({
  meals,
  addToFavorites,
  removeFromFavorites,
  refetch,
}) => {
  const { t } = useTranslator();
  const [active, setActive] = useState('all');
  const [search, setSearch] = useState('');
  const [useSearchResult, setUseSearchResult] = useState(false);
  const [hasEverSearch, setHasEverSearch] = useState(false);

  const {
    images: { favouritesIcon, noSearchResultsIcon },
  } = useAppContext();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHasEverSearch(true);
    setSearch(event.target.value);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setUseSearchResult(true);

    const searchCriteria = {
      query: search,
      favoriteMeals: true,
    };

    await refetch({ searchCriteria });

    if (!search) {
      setHasEverSearch(false);
    }
  };

  const handleReset = async () => {
    setSearch('');
    setUseSearchResult(false);

    const searchCriteria = {
      query: '',
      favoriteMeals: true,
    };

    await refetch({ searchCriteria });
  };

  const handleRefetch = async () => {
    const searchCriteria = {
      query: search,
      favoriteMeals: true,
    };

    await refetch({ searchCriteria });
  };

  const renderFavMeals = () => {
    if (meals) {
      if (useSearchResult) {
        if (meals.length === 0) {
          return (
            <div className="text-center pt-xl">
              <img src={noSearchResultsIcon} width={64} height={64} className="mb-m" alt="" />
              <p className="text-text-variant-color mb-m">
                {t('meal.searchResults.noResultsContent')}
              </p>
              <p className="text-text-variant-color">
                {t('meal.searchResults.noResultsAdditionalContent')}
              </p>
            </div>
          );
        }
      }

      if (meals.length > 0) {
        const filteredFavMeals = meals.filter(
          (meal) => meal.node.tags.mealTypeTags[0].name === active || active === 'all'
        );

        return (
          <Row className="cookbook-fav-meals">
            {filteredFavMeals.map(({ node }) => (
              <Col xs="6" md="3" key={node.id}>
                <CookbookMealItem
                  {...node}
                  addToFavorites={addToFavorites}
                  removeFromFavorites={removeFromFavorites}
                  handleRefetch={handleRefetch}
                  orientation="vertical"
                  displayRating={false}
                />
              </Col>
            ))}
          </Row>
        );
      }

      return (
        <Row className="align-items-center full-height3">
          <Col xs={{ size: 10, offset: 1 }} md={{ size: 6, offset: 3 }} className="text-center">
            <img src={favouritesIcon} alt="" />
            <p className="text-text-variant-color mt-2">
              {t('cookbook.favorites.noMeals.content')}
            </p>
          </Col>
        </Row>
      );
    }

    return '';
  };

  return (
    <Layout>
      <TitleBar level={1}>{t(`cookbook.favorites.navbarTitle`)}</TitleBar>
      <div className="page-template-cookbook">
        <Container className="py-md-4">
          <Row>
            <Col xl={{ size: 10, offset: 1 }}>
              <div className="page-container">
                {(meals.length > 0 || search || hasEverSearch) && (
                  <FavMealsFilters
                    meals={meals}
                    active={active}
                    setActive={setActive}
                    search={search}
                    handleChange={handleChange}
                    handleSubmit={handleSubmit}
                    handleReset={handleReset}
                    useSearchResult={useSearchResult}
                  />
                )}
                <div className="fav-result-container">
                  {useSearchResult && (
                    <h2 className="title-medium semi-bold mb-m">{t('meal.searchResults.title')}</h2>
                  )}
                  {renderFavMeals()}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </Layout>
  );
};

const Placeholder = () => <UniversalPlaceholder titleBar="" />;

export default withLoading(CookbookFavMealsContainer, Placeholder);
